import { useQuery } from "@tanstack/react-query";
import { usePymbleApi } from "./usePymbleApi";
import httpService from "../services/httpService";

/**
 * Interfaces
 *
 * Expected format of response from StorageContainerSas
 */
export interface StorageContainerSasResponse {
  ContainerUrl?: string;
  SasExpiry?: string;
  SasQueryString?: string;
}
/* Format of final storage detail result */
export interface StorageContainerSasJson extends StorageContainerSasResponse {
  Url: string;
}

export type StorageEndpointType = {
  profile: string;
  secure: string;
};

/* Allowed endpoints */
export const StorageEndpoint: StorageEndpointType = {
  profile: "/storage/sas/?container=profile-images",
  secure: "/storage/sas/?container=secure",
};

/**
 * Hook to call APIs to generate a Share Access Secret and url for secure storage retrieval
 * on Azure Storage endpoints.  Can be used for secure file locations or profile images.
 *
 * @param {string} storageEndpoint Endpoint for SAS generation (eg /secure/sas | /profile-image/sas )
 * @param {string} fileName Relative path + name of file. Optional.
 * @param {boolean} checkFileExists Perform a HEAD check on resource to validate it.
 *                                  If HEAD check fails, then url returned will be blank.
 *                                  Optional.
 *
 * @returns { storageDetail, azureStorageLoaded }
 */
export function useAzureStorage(
  storageEndpoint: string,
  fileName: string = "",
  checkFileExists: boolean = false
) {
  const { callApi, isAuthenticated } = usePymbleApi();

  const { data: storageDetail, isLoading } = useQuery({
    queryKey: ["sas", storageEndpoint, fileName, checkFileExists],
    queryFn: async (): Promise<StorageContainerSasJson> => {
      const json: StorageContainerSasJson = {
        Url: "",
        ContainerUrl: "",
        SasQueryString: "",
        SasExpiry: "",
      };

      try {
        const token = await callApi("GET", storageEndpoint) as StorageContainerSasResponse;

        if (token && token.SasQueryString) {
          const result: StorageContainerSasJson = {
            Url: fileName
              ? `${token.ContainerUrl}/${fileName}?${token.SasQueryString}`
              : `${token.ContainerUrl}?${token.SasQueryString}`,
            ...token,
          };

          if (checkFileExists && result.Url) {
            try {
              await httpService.head(result.Url);
            } catch {
              // Do NOT log the URL: it carries the contact-derived blob
              // filename and the signed SAS query string (Codex review,
              // gm#127 - missing profile images 404 here routinely).
              console.debug("Storage HEAD check failed; clearing url.");
              result.Url = "";
            }
          }

          return result;
        }
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : String(e);
        console.error(`Could not retrieve storage container url. ${msg}`);
      }

      return json;
    },
    enabled: isAuthenticated,
    staleTime: Infinity,
  });

  return {
    storageDetail,
    azureStorageLoaded: !isLoading,
  };
}
